import classNames from 'classnames';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useEffectEvent } from 'use-effect-event';
import { useQuery } from '@apollo/client';
import PageLayout from '../app/layout/PageLayout';
import { useSidebarOpenState } from '../app/layout/SidebarOpenStateContext';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button, { IconButton } from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import { gql } from '../graphql/generated/gql';
import { PlanTier } from '../graphql/generated/graphql';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import sidebarToggleIcon from '../icons/non-prefilled/16x16/sidebarToggle.svg';
import NotificationDot from '../notification/NotificationDot';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import { isElectron } from '../utils/desktopClient';
import useSlug from '../utils/router/useSlug';
import { openPricingModal } from '../utils/url/pricingPath';
import useShortcut from '../utils/useShortcut';
import * as styles from './InternalTopbar.module.scss';
import SearchBar, { useSearchBar } from './SearchBar';
import TopbarCreatePostButton from './TopbarCreatePostButton';

const FULL_SEARCH_CLASSNAME = styles.fullSearch;

const InnerContext = React.createContext<{
  setIsShowingMobileFullSearch: React.Dispatch<React.SetStateAction<boolean>>;
  internalTopbarRef: React.RefObject<HTMLDivElement | null>;
}>({
  setIsShowingMobileFullSearch: () => {},
  internalTopbarRef: React.createRef(),
});

const UNREAD_NOTIFICATIONS_QUERY = gql(/** @complexity 2 */ `
  query unreadNotifications {
    unreadNotifications(first: 0) {
      totalCount
    }
  }
`);

interface ContainerProps {
  children: React.ReactNode;
}

const Container = ({ children }: ContainerProps) => {
  const internalTopbarRef = useRef<HTMLDivElement | null>(null);
  const [isShowingMobileFullSearch, setIsShowingMobileFullSearch] =
    useState(false);

  const value = useMemo(
    () => ({ setIsShowingMobileFullSearch, internalTopbarRef }),
    [setIsShowingMobileFullSearch],
  );

  return (
    <InnerContext.Provider value={value}>
      <PageLayout.Topbar
        ref={internalTopbarRef}
        className={classNames(styles.internalTopbar, {
          [FULL_SEARCH_CLASSNAME]: isShowingMobileFullSearch,
        })}
      >
        {children}
      </PageLayout.Topbar>
    </InnerContext.Provider>
  );
};

const SidebarToggle = () => {
  const { isSidebarOpen, toggleSidebar } = useSidebarOpenState();

  const { data: unreadNotificationData } = useQuery(
    UNREAD_NOTIFICATIONS_QUERY,
    {
      skip: isSidebarOpen,
    },
  );

  // TODO: This counts HistoryNotification and InvitationNotification
  // that aren't displayed in the new notifications design
  const hasNewNotifications =
    !!unreadNotificationData?.unreadNotifications?.totalCount;

  return (
    <div className={styles.sidebarToggle}>
      <IconButton
        onClick={() => {
          toggleSidebar();
        }}
        aria-label="Toggle toolbar"
        size="xl"
      >
        <SVGIcon size="large">{sidebarToggleIcon}</SVGIcon>
        {!isSidebarOpen && hasNewNotifications && (
          <div className={styles.notificationButtonWrapper}>
            <NotificationDot />
          </div>
        )}
      </IconButton>
    </div>
  );
};

interface ContainerProps {
  children: React.ReactNode;
}

const SearchBarContainer = ({ children }: ContainerProps) => {
  const { isExpanded } = useSearchBar();

  return (
    <div
      className={classNames(styles.searchContainer, {
        [styles.isExpanded]: isExpanded,
      })}
    >
      {children}
    </div>
  );
};

const Search = () => {
  const { setIsShowingMobileFullSearch, internalTopbarRef } =
    useContext(InnerContext);
  const [isQuickJumpSearch, setIsQuickJumpSearch] = useState(false);
  const searchInputRef = useRef<{ focus(): void } | null>(null);

  const showAndFocusSearch = (quickJump = false) => {
    setIsShowingMobileFullSearch(true);
    setIsQuickJumpSearch(quickJump);

    // Manipulate classes directly to allow auto-focus on mobile Safari.
    internalTopbarRef.current?.classList.add(FULL_SEARCH_CLASSNAME);
    searchInputRef.current?.focus();
  };

  useShortcut('/', (e) => {
    e.preventDefault();
    showAndFocusSearch();
  });

  useShortcut('mod+k', (e) => {
    if (!isElectron) {
      e.preventDefault();
      showAndFocusSearch(true);
    }
  });

  const onQuickJump = useEffectEvent(() => {
    showAndFocusSearch(true);
  });
  useEffect(() => {
    return window.desktop?.onFeatureRequested?.('quickJump', () => {
      onQuickJump();
    });
  }, []);

  return (
    <>
      <SearchBar.Root
        onExpandedChange={(expanded) => {
          if (!expanded) {
            setIsQuickJumpSearch(false);
          }
        }}
      >
        <SearchBarContainer>
          <div className={styles.searchInputContainer}>
            <SearchBar.Input
              ref={searchInputRef}
              quickJump={isQuickJumpSearch}
            />
          </div>
          <div className={styles.mobileSearchCancel}>
            <Button onClick={() => setIsShowingMobileFullSearch(false)}>
              Cancel
            </Button>
          </div>
        </SearchBarContainer>
      </SearchBar.Root>
      <div className={styles.mobileSearchButton}>
        <IconButton
          onClick={() => showAndFocusSearch()}
          aria-label="Show search"
          size="xl"
        >
          {searchIcon}
        </IconButton>
      </div>
    </>
  );
};

interface DesktopActionsProps {
  postActions?: React.ReactNode;
}

const DesktopActions = ({ postActions }: DesktopActionsProps) => {
  const [postId] = useSlug('postSlug');
  const [topicId] = useSlug('topicSlug');

  const dispatch = useAppDispatch();
  const location = useLocation();

  const isAdmin =
    useAppSelector((state) => state.session.user).type === 'ADMIN';
  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  );

  return (
    <div className={styles.actions}>
      {isAdmin && planTier === PlanTier.Free && location.pathname === '/' && (
        <div className={styles.upgradeText}>
          <span className={styles.upgradeTextInfo}>
            Collaborate with unlimited people.{' '}
          </span>
          <Button
            variant="link"
            onClick={() => openPricingModal(dispatch, 'users')}
          >
            Upgrade now
          </Button>
        </div>
      )}
      {postId ? postActions : <TopbarCreatePostButton topicId={topicId} />}
    </div>
  );
};

interface MobileActionsProps {
  postActions?: React.ReactNode;
}

const MobileActions = ({ postActions }: MobileActionsProps) => {
  const [postId] = useSlug('postSlug');
  const [topicId] = useSlug('topicSlug');

  return (
    <div className={classNames(styles.actions, styles.mobile)}>
      {postId ? postActions : <TopbarCreatePostButton topicId={topicId} />}
    </div>
  );
};

interface InternalTopbarProps {
  postActions?: React.ReactNode;
}

const InternalTopbar = ({ postActions }: InternalTopbarProps) => {
  const isMobileLayout = useIsLayoutSize(LayoutSize.mobile);

  return (
    <Container>
      <SidebarToggle />
      <Search />
      {isMobileLayout ? (
        <MobileActions postActions={postActions} />
      ) : (
        <DesktopActions postActions={postActions} />
      )}
    </Container>
  );
};

export default InternalTopbar;
